export type EmployeeProtectedRelation = {
  type?: string;
  label?: string;
  count?: number;
};

export type EmployeeWithRelations = {
  id?: string;
  name?: string;
  code?: string;
  relations?: EmployeeProtectedRelation[];
};

const formatEmployeeRelationLine = (employee: EmployeeWithRelations): string => {
  const code = String(employee.code || '').trim();
  const name = String(employee.name || '').trim();
  const heading = [code, name].filter(Boolean).join(' — ') || 'Nhân viên';

  const relationParts = (Array.isArray(employee.relations) ? employee.relations : [])
    .map((relation) => {
      const label = String(relation.label || relation.type || 'liên kết').trim();
      const count = Number(relation.count);
      if (Number.isFinite(count) && count > 0) {
        return `${label} (${count})`;
      }
      return label;
    })
    .filter(Boolean);

  if (relationParts.length === 0) {
    return heading;
  }

  return `${heading}: ${relationParts.join(', ')}`;
};

/** Format API error/partial response when permanent delete is blocked by relations. */
export const formatEmployeePermanentDeleteError = (
  data: unknown,
  fallback = 'Có lỗi xảy ra khi xóa vĩnh viễn nhân viên!',
): string => {
  if (!data || typeof data !== 'object') {
    return fallback;
  }

  const record = data as Record<string, unknown>;
  const employeesWithRelations = Array.isArray(record.employees_with_relations)
    ? (record.employees_with_relations as EmployeeWithRelations[])
    : [];

  const lines: string[] = [];

  const message =
    typeof record.message === 'string' && record.message.trim()
      ? record.message.trim()
      : '';
  const error =
    typeof record.error === 'string' && record.error.trim() ? record.error.trim() : '';

  if (message) {
    lines.push(message);
  } else if (error) {
    lines.push(error);
  }

  employeesWithRelations.forEach((employee) => {
    lines.push(formatEmployeeRelationLine(employee));
  });

  if (lines.length > 0) {
    return lines.join('\n');
  }

  return fallback;
};

export const hasEmployeePermanentDeleteRelations = (data: unknown): boolean => {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const employees = (data as Record<string, unknown>).employees_with_relations;
  return Array.isArray(employees) && employees.length > 0;
};
